import { Box, ListSubheader, Typography } from '@material-ui/core'
import Add from '@material-ui/icons/Add'
import { Fragment, useEffect, useMemo, useState } from 'react'
import { Controller } from 'react-hook-form'
import { useDebounce } from 'usehooks-ts'

import { Autocomplete } from '@/components/auto-complete'
import Button from '@/components/button'
import { useGetCalendarDiagnoses } from '@/hooks/queries/use-get-calendar-diagnoses'

import { StepKeys } from '../..'
import { useNewActivityForm } from '../../new-activity-form'
import { useStyles } from './styles'

export interface PulverizationInfoStepProps {
  onChangeCurrentStep: (step: StepKeys) => void
}

export function PulverizationInfoStep({
  onChangeCurrentStep,
}: PulverizationInfoStepProps) {
  const classes = useStyles()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 1000)

  const {
    control,
    watch,
    reset,
    formState: { isSubmitting },
  } = useNewActivityForm()

  const getCalendarDiagnosesQuery = useGetCalendarDiagnoses({
    search: debouncedSearch,
    type: 'pulverization',
  })

  function handleCancel() {
    onChangeCurrentStep('activity_type')
  }

  const diagnosis = watch('diagnosis')

  const calendarDiagnoses = useMemo(
    () => [
      { id: 'new-mapping', name: 'Cadastrar novo mapeamento' },
      ...(getCalendarDiagnosesQuery.data?.diagnoses || []),
    ],
    [getCalendarDiagnosesQuery.data],
  )

  useEffect(() => {
    if (diagnosis?.id === 'new-mapping') {
      reset()
      handleCancel()
    }
  }, [diagnosis])

  return (
    <div className={classes.container}>
      <Typography variant='h1'>Informações básicas</Typography>

      <div className={classes.inputsContainer}>
        <Controller
          control={control}
          name='diagnosis'
          defaultValue={null as any}
          render={({ field: { onChange, value }, fieldState: { error } }) => (
            <Autocomplete
              id='mapping'
              classes={{ listbox: classes.menuList }}
              options={calendarDiagnoses}
              onChange={(_, newValue) => onChange(newValue)}
              value={value}
              freeSolo
              loading={getCalendarDiagnosesQuery.isFetching}
              groupBy={() => 'diagnosis'}
              renderGroup={(params) => (
                <Fragment key={params.key}>
                  <ListSubheader>Diagnósticos</ListSubheader>
                  {params.children}
                </Fragment>
              )}
              getOptionLabel={(option) => option.name}
              getOptionSelected={(option, value) => option.id === value.id}
              renderOption={(option) =>
                option.id === 'new-mapping' ? (
                  <Box display='flex' gridGap='3rem'>
                    <Add />
                    <Typography variant='subtitle1'>{option.name}</Typography>
                  </Box>
                ) : (
                  option.name
                )
              }
              filterOptions={(options, params) =>
                options.filter((option) => {
                  if (!params.inputValue) return true

                  const searchValue = params.inputValue.toLowerCase()

                  return option.name.toLowerCase().includes(searchValue)
                })
              }
              optionsError={
                !getCalendarDiagnosesQuery.isFetching &&
                getCalendarDiagnosesQuery.isError
              }
              noOptionsText='Não foi encontrado mapeamentos para a companhia atual.'
              inputTextProps={{
                label: 'Selecione o diagnóstico',
                required: true,
                error:
                  (!getCalendarDiagnosesQuery.isFetching &&
                    getCalendarDiagnosesQuery.isError) ||
                  !!error,
                helperText:
                  !getCalendarDiagnosesQuery.isFetching &&
                  getCalendarDiagnosesQuery.isError
                    ? 'Não foi encontrado mapeamentos para a companhia atual.'
                    : error?.message,
              }}
              onInputChange={(_, newInputValue) => {
                setSearch(newInputValue)
              }}
            />
          )}
        />

        <div className={classes.buttonsContainer}>
          <Button outlined disabled={isSubmitting} onClick={handleCancel}>
            Cancelar
          </Button>
          <Button type='submit' loading={isSubmitting}>
            Avançar
          </Button>
        </div>
      </div>
    </div>
  )
}
